<script setup lang="ts">
import { type ComponentExposed } from 'vue-component-type-helpers';
import { type DateSpanObject, type SearchMode } from '@utils/types';
import type { LabeledPriceObject } from '@search/ItineraryPriceLoader';
import { type ReserveType } from '@utils/itinerary';
import { MrDatepicker } from '#components';

const { reserveType, searchMode } = defineProps<{
    prices?: LabeledPriceObject[];
    joined?: boolean;
    canChangeRange?: boolean;
    startDayTitle?: string;
    endDayTitle?: string;
    unequalRange?: boolean;
    desktopInRowInputs?: boolean;
    countNights?: boolean;
    reserveType: ReserveType;
    loadingPrice?: boolean;
    searchMode: SearchMode;
}>();
const emit = defineEmits<{
    pageChange: [date: Date];
    submit: [];
}>();
const isMounted = useMounted();
const modelValue = defineModel<DateSpanObject | null>({
    get(v) {
        if (!isMounted.value) {
            return {
                start: null,
                end: null,
            };
        }
        return v;
    },
});
const range = defineModel<'hidden' | 'enabled' | 'disabled'>('range');
const datepicker = ref<ComponentExposed<typeof MrDatepicker>>();
const analyticsStore = useAnalyticsStore();
const { emitListener, addEventListener } = addListener();

const onBack = () => {
    analyticsStore.trackPosthog('search-form-back', {
        reserveType,
        searchMode,
        inputComponent: 'date-picker',
    });
};

const onClose = () => {
    analyticsStore.trackPosthog('search-form-close', {
        reserveType,
        searchMode,
        inputComponent: 'date-picker',
    });
    emitListener('btn-close');
};

defineExpose({
    focus: () => datepicker.value?.focus(),
    hide: (onlyIfOnDesktop: boolean, done?: boolean) =>
        datepicker.value?.hide(onlyIfOnDesktop, done),
    addEventListener,
});
</script>
<template>
    <mr-datepicker
        ref="datepicker"
        v-model="modelValue"
        v-model:range="range"
        format="long"
        :min-date="new Date()"
        :prices
        supports-gregorian
        hide-description
        hide-label
        :joined
        :can-change-range
        :start-day-title
        :end-day-title
        :unequal-range
        :desktop-in-row-inputs
        :count-nights
        double-month
        :loading-price
        @page-change="emit('pageChange', $event)"
        @btn-close="onClose"
        @back="onBack"
        @submit="emit('submit')"
        @open="emitListener('open')"
        @done="emitListener('done')"
    />
</template>
